import type { CSSProperties, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CalendarDays, Eye, Image as ImageIcon, Loader2, MapPinned, RefreshCcw, Search } from 'lucide-react'
import { PageTitle } from 'shared/components/layout/PageTitle'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Input } from 'shared/ui/input'
import { DatePicker } from 'modules/permit/components/DatePicker'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { AsyncSearchableSelect } from 'shared/ui/async-searchable-select'
import { Switch } from 'shared/ui/switch'
import { Badge } from 'shared/ui/badge'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'shared/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'shared/ui/dialog'
import { getAvailablePermitTypes, type PermitType } from 'shared/api/templateService'
import { getContractorCompanies, type ContractorCompany } from 'modules/permit/api/contractorService'
import { PERMIT_STATUS_CODES } from 'modules/permit/constants/permitStatus'
import {
  getPermitDetail,
  getOverallDashboardPermits,
  getOverallDashboardSummary,
  getOverallDashboardWorkers,
  getSubactivityOptions,
  getProjectNoOptions,
  getWorkOrderNoOptions,
  type OverallDashboardSummary,
  type OverallDashboardWorkerItem,
  type PermitDetail,
  type PermitListItem,
  type SubactivityOption,
} from 'modules/permit/api/permitService'
import { fetchWorkAreas, type PlotPlanDto, type WorkAreaOption } from 'modules/admin/api/plotPlanService'
import { getDefaultGps, getPlotPlans, type PlotPlanGps } from 'modules/permit/api/workAreaService'
import { getPermitStatusBadgeClass, getPermitStatusLabel } from 'modules/permit/constants/permitStatus'
import { getPersistedSelectedSiteId, resolveTenantCandidate, resolveTenantId } from 'shared/utils/tenantContext'

interface LayoutState {
  user?: Record<string, unknown>
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    id?: string
    name?: string
  }
}

type DashboardLayoutMode = 'plot-plan' | 'map'

type DashboardFilters = {
  keyword: string
  dateFrom: string
  dateTo: string
  companyId: string
  workAreaIds: string[]
  permitType: string
  statusCategory: string
  inertGasArea: string
  toolType: string
  showLayout: boolean
  layoutMode: DashboardLayoutMode
  offHoursOnly: boolean
  projectNo: string
  workOrderNo: string
  subactivityCodes: string[]
}

type DashboardPermitRecord = {
  permit: PermitListItem
  detail: PermitDetail | null
}

type PlotArea = {
  xPercent: number
  yPercent: number
  widthPercent: number
  heightPercent: number
}

type GpsArea = {
  north: number
  south: number
  east: number
  west: number
}

const STATUS_SCOPE = [
  PERMIT_STATUS_CODES.READY_TO_OPEN,
  PERMIT_STATUS_CODES.OPEN,
  PERMIT_STATUS_CODES.CLOSED,
  PERMIT_STATUS_CODES.CANCELLED,
]
const CARD_PERMIT_TYPE_CODES = {
  coldWork: 'COLD_WORK',
  confineWork: 'CONFINE_WORK',
  hotWork: 'HOT_WORK',
} as const
const CARD_STATUS_CODES = {
  waitForOpen: PERMIT_STATUS_CODES.READY_TO_OPEN,
  opened: PERMIT_STATUS_CODES.OPEN,
  closed: PERMIT_STATUS_CODES.CLOSED,
  cancelled: PERMIT_STATUS_CODES.CANCELLED,
} as const
const CARD_INERT_GAS_VALUES = {
  inertGasArea: 'Y',
  nonInertGasArea: 'N',
} as const
const CARD_TOOL_TYPE_VALUES = {
  openFire: 'OPF',
  nonOpenFire: 'NPF',
} as const
const DEFAULT_PAGE_SIZE = 50
const PAGE_SIZE_OPTIONS = ['10', '20', '50']
const DEFAULT_GPS_MAP_ZOOM = 18
const MAX_GPS_AREA_SPAN = 1
const MAX_GPS_AREA_REFERENCE_DISTANCE = 1
const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
const EMPTY_DASHBOARD_SUMMARY: OverallDashboardSummary = {
  totalPermit: 0,
  totalWorker: 0,
  coldWork: 0,
  confineWork: 0,
  confineWorkInertGasArea: 0,
  confineWorkNonInertGasArea: 0,
  hotWork: 0,
  hotWorkOpenFire: 0,
  hotWorkNonOpenFire: 0,
  waitForOpen: 0,
  opened: 0,
  closed: 0,
  cancelled: 0,
}

const getTodayDateValue = () => {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

const toDateFilterStart = (value?: string | null) => {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed || undefined
}

const toDateFilterEnd = (value?: string | null) => {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return undefined
  }

  // When the UI sends only YYYY-MM-DD, treat the end date as the end of that day
  // so single-day searches still include every record within the selected date.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T23:59:59.999`
  }

  return trimmed
}

const createDefaultFilters = (): DashboardFilters => ({
  keyword: '',
  dateFrom: getTodayDateValue(),
  dateTo: getTodayDateValue(),
  companyId: 'ALL',
  workAreaIds: [],
  permitType: 'ALL',
  statusCategory: 'ALL',
  inertGasArea: 'ALL',
  toolType: 'ALL',
  showLayout: false,
  layoutMode: 'plot-plan',
  offHoursOnly: false,
  projectNo: 'ALL',
  workOrderNo: 'ALL',
  subactivityCodes: [],
})

const normalizeGuid = (value?: string | null) => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return GUID_REGEX.test(trimmed) ? trimmed.toLowerCase() : null
}

const normalizeUserType = (value?: unknown) => {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim().toUpperCase()
}

const resolveAssetUrl = (path?: string | null) => {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  const normalised = path.replace(/\\/g, '/').replace(/^\/+/, '')
  const lower = normalised.toLowerCase()
  const withPrefix = lower.startsWith('images/')
    ? normalised
    : lower.startsWith('attachments/')
      ? normalised
      : lower.startsWith('plotplans/')
        ? `attachments/${normalised}`
        : `images/${normalised}`
  const baseUrl = process.env.REACT_APP_API_BASE_URL
  if (baseUrl) {
    return `${baseUrl.replace(/\/$/, '')}/${withPrefix}`
  }
  return `/${withPrefix}`
}

const isPlotArea = (value: unknown): value is PlotArea =>
  Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as PlotArea).xPercent === 'number' &&
      typeof (value as PlotArea).yPercent === 'number' &&
      typeof (value as PlotArea).widthPercent === 'number' &&
      typeof (value as PlotArea).heightPercent === 'number',
  )

const isGpsArea = (value: unknown): value is GpsArea =>
  Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as GpsArea).north === 'number' &&
      typeof (value as GpsArea).south === 'number' &&
      typeof (value as GpsArea).east === 'number' &&
      typeof (value as GpsArea).west === 'number',
  )

const hasCoordinate = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const normalizeGpsArea = (area: GpsArea) => ({
  north: Math.max(area.north, area.south),
  south: Math.min(area.north, area.south),
  east: Math.max(area.east, area.west),
  west: Math.min(area.east, area.west),
})

const isReasonableGpsArea = (area: GpsArea) => {
  const normalized = normalizeGpsArea(area)
  return (
    Math.abs(normalized.north - normalized.south) <= MAX_GPS_AREA_SPAN &&
    Math.abs(normalized.east - normalized.west) <= MAX_GPS_AREA_SPAN
  )
}

const isGpsAreaNearReference = (
  area: GpsArea,
  referenceLatitude?: number | null,
  referenceLongitude?: number | null,
) => {
  if (!hasCoordinate(referenceLatitude) || !hasCoordinate(referenceLongitude)) {
    return true
  }

  const normalized = normalizeGpsArea(area)
  const centerLatitude = (normalized.north + normalized.south) / 2
  const centerLongitude = (normalized.east + normalized.west) / 2

  return (
    Math.abs(centerLatitude - referenceLatitude) <= MAX_GPS_AREA_REFERENCE_DISTANCE &&
    Math.abs(centerLongitude - referenceLongitude) <= MAX_GPS_AREA_REFERENCE_DISTANCE
  )
}

const formatWorkDate = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('th-TH')
}

const formatWorkPeriod = (start?: string | null, end?: string | null) => {
  if (!start && !end) return '-'
  const startDate = start ? new Date(start) : null
  const endDate = end ? new Date(end) : null
  const dateLabel =
    startDate && !Number.isNaN(startDate.getTime())
      ? startDate.toLocaleDateString('th-TH')
      : start || '-'
  const startTime =
    startDate && !Number.isNaN(startDate.getTime())
      ? startDate.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
      : '--:--'
  const endTime =
    endDate && !Number.isNaN(endDate.getTime())
      ? endDate.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
      : '--:--'
  return `${dateLabel} ${startTime} - ${endTime}`
}

const classifyPermitType = (value?: string | null) => {
  const normalized = (value ?? '').trim().toLowerCase()
  if (!normalized) return 'other'
  if (normalized.includes('cold')) return 'cold'
  if (normalized.includes('confine')) return 'confine'
  if (normalized.includes('hot')) return 'hot'
  if (normalized.includes('excav')) return 'excavation'
  if (normalized.includes('isolat')) return 'isolation'
  return 'other'
}

const getPermitTypeBadgeClass = (value?: string | null) => {
  switch (classifyPermitType(value)) {
    case 'cold':
      return 'border-blue-200 bg-blue-50 text-blue-700'
    case 'confine':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'hot':
      return 'border-red-200 bg-red-50 text-red-700'
    case 'excavation':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'isolation':
      return 'border-violet-200 bg-violet-50 text-violet-700'
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700'
  }
}

const getPermitTypeTone = (value?: string | null) => {
  switch (classifyPermitType(value)) {
    case 'cold':
      return {
        stroke: '#3b82f6',
        fill: 'rgba(59, 130, 246, 0.14)',
        badge: '#2563eb',
      }
    case 'confine':
      return {
        stroke: '#f59e0b',
        fill: 'rgba(245, 158, 11, 0.16)',
        badge: '#d97706',
      }
    case 'hot':
      return {
        stroke: '#ef4444',
        fill: 'rgba(239, 68, 68, 0.15)',
        badge: '#dc2626',
      }
    case 'excavation':
      return {
        stroke: '#10b981',
        fill: 'rgba(16, 185, 129, 0.14)',
        badge: '#059669',
      }
    case 'isolation':
      return {
        stroke: '#8b5cf6',
        fill: 'rgba(139, 92, 246, 0.14)',
        badge: '#7c3aed',
      }
    default:
      return {
        stroke: '#64748b',
        fill: 'rgba(100, 116, 139, 0.14)',
        badge: '#475569',
      }
  }
}

const buildLocationDescription = (option?: WorkAreaOption | null) => {
  if (!option) return '-'
  return option.name || option.code || option.locationId || '-'
}

const chunk = <T,>(items: T[], size: number) => {
  const groups: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    groups.push(items.slice(index, index + size))
  }
  return groups
}

const twoLineClampStyle: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

function SummaryHeroCard({
  title,
  value,
  toneClassName,
  children,
  onClick,
  active,
}: {
  title: string
  value: string | number
  toneClassName: string
  children?: ReactNode
  onClick?: () => void
  active?: boolean
}) {
  return (
    <Card
      className={`overflow-hidden border-0 shadow-sm ${onClick ? 'cursor-pointer transition-transform hover:scale-[1.02]' : ''} ${active ? 'ring-4 ring-offset-2 ring-slate-900/40' : ''}`}
      onClick={onClick}
    >
      <CardContent className={`h-full rounded-2xl border p-5 ${toneClassName}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] opacity-80">{title}</p>
          </div>
          <p className="text-4xl font-bold leading-none">{value}</p>
        </div>
        {children ? <div className="mt-4">{children}</div> : null}
      </CardContent>
    </Card>
  )
}

function SummarySubCard({
  title,
  value,
  className,
  onClick,
  active,
}: {
  title: string
  value: string | number
  className: string
  onClick?: (event: MouseEvent) => void
  active?: boolean
}) {
  return (
    <div
      className={`rounded-xl px-3 py-3 ${className} ${onClick ? 'cursor-pointer transition-transform hover:scale-[1.03]' : ''} ${active ? 'ring-4 ring-offset-2 ring-slate-900/40' : ''}`}
      onClick={onClick}
    >
      <p className="text-center text-xl font-bold leading-none">{value}</p>
      <p className="mt-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em]">{title}</p>
    </div>
  )
}

function SummaryStatusCard({
  title,
  subtitle,
  value,
  valueClassName,
  cardClassName,
  onClick,
  active,
}: {
  title: string
  subtitle: string
  value: string | number
  valueClassName: string
  cardClassName: string
  onClick?: () => void
  active?: boolean
}) {
  return (
    <Card
      className={`shadow-sm ${cardClassName} ${onClick ? 'cursor-pointer transition-transform hover:scale-[1.02]' : ''} ${active ? 'ring-4 ring-offset-2 ring-slate-900/40' : ''}`}
      onClick={onClick}
    >
      <CardContent className="flex items-start justify-between gap-4 p-4">
        <div>
          <p className="text-sm font-medium text-slate-700">{title}</p>
          <p className="mt-1 text-[11px] text-slate-500">{subtitle}</p>
        </div>
        <p className={`text-4xl font-bold leading-none ${valueClassName}`}>{value}</p>
      </CardContent>
    </Card>
  )
}

async function loadPermitDetails(tenantId: string, permits: PermitListItem[]) {
  const result = new Map<string, PermitDetail | null>()
  const groups = chunk(permits, 10)

  for (const group of groups) {
    const items = await Promise.all(
      group.map(async (permit) => {
        try {
          const detail = await getPermitDetail(tenantId, permit.permitId)
          return [permit.permitId, detail] as const
        } catch {
          return [permit.permitId, null] as const
        }
      }),
    )
    items.forEach(([permitId, detail]) => result.set(permitId, detail))
  }

  return result
}

function PlotPlanLayoutView({
  imageUrl,
  records,
  onSelect,
}: {
  imageUrl: string
  records: DashboardPermitRecord[]
  onSelect: (record: DashboardPermitRecord) => void
}) {
  const overlays = useMemo(() => {
    return records.flatMap((record, index) => {
      const areasRaw = record.detail?.workArea?.areas ?? (record.detail?.workArea?.area ? [record.detail.workArea.area] : [])
      const areas = areasRaw.filter(isPlotArea)
      const marker = record.detail?.workArea?.marker
      return [
        ...areas.map((area, areaIndex) => ({
          key: `${record.permit.permitId}-area-${areaIndex}`,
          type: 'area' as const,
          record,
          area,
          index,
        })),
        ...(marker && hasCoordinate(marker.xPercent) && hasCoordinate(marker.yPercent)
          ? [
              {
                key: `${record.permit.permitId}-marker`,
                type: 'marker' as const,
                record,
                marker,
                index,
              },
            ]
          : []),
      ]
    })
  }, [records])

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
        <img
          src={imageUrl}
          alt="Plot Plan"
          className="h-auto max-h-[720px] w-full object-contain"
          draggable={false}
        />
        <div className="pointer-events-none absolute inset-0">
          {overlays.map((overlay) => {
            if (overlay.type === 'area') {
              const tone = getPermitTypeTone(overlay.record.permit.permitType)
              const style: CSSProperties = {
                left: `${overlay.area.xPercent}%`,
                top: `${overlay.area.yPercent}%`,
                width: `${overlay.area.widthPercent}%`,
                height: `${overlay.area.heightPercent}%`,
                borderColor: tone.stroke,
                backgroundColor: tone.fill,
              }
              return (
                <button
                  key={overlay.key}
                  type="button"
                  className="pointer-events-auto absolute rounded-md border-2 transition hover:brightness-95"
                  style={style}
                  onClick={() => onSelect(overlay.record)}
                  title={overlay.record.permit.permitNumber}
                >
                  <span
                    className="absolute left-1 top-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                    style={{ backgroundColor: tone.badge }}
                  >
                    {overlay.index + 1}
                  </span>
                </button>
              )
            }

            const style: CSSProperties = {
              left: `${overlay.marker.xPercent}%`,
              top: `${overlay.marker.yPercent}%`,
            }
            return (
              <button
                key={overlay.key}
                type="button"
                className="pointer-events-auto absolute -translate-x-1/2 -translate-y-full"
                style={style}
                onClick={() => onSelect(overlay.record)}
                title={overlay.record.permit.permitNumber}
              >
                <div className="rounded-full bg-rose-500 px-2 py-1 text-[11px] font-semibold text-white shadow-lg">
                  {overlay.index + 1}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {records.map((record, index) => (
          <button
            key={record.permit.permitId}
            type="button"
            onClick={() => onSelect(record)}
            className="rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-sky-300 hover:bg-sky-50"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700">
                {index + 1}
              </span>
              <Badge variant="outline" className={getPermitStatusBadgeClass(record.permit.status)}>
                {getPermitStatusLabel(record.permit.status, record.permit.statusName)}
              </Badge>
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-900">{record.permit.permitNumber}</p>
            <p className="text-sm text-slate-600">{record.permit.title || record.permit.workDescription || '-'}</p>
          </button>
        ))}
      </div>
    </div>
  )
}

function GpsLayoutView({
  records,
  defaultGps,
  onSelect,
}: {
  records: DashboardPermitRecord[]
  defaultGps?: PlotPlanGps | null
  onSelect: (record: DashboardPermitRecord) => void
}) {
  const mapElementRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.TileLayer | null>(null)
  const overlaysRef = useRef<L.Layer[]>([])
  const [mapViewMode, setMapViewMode] = useState<'street' | 'satellite'>('satellite')

  const rawDefaultGpsLatitude = defaultGps?.latitude
  const rawDefaultGpsLongtitude = defaultGps?.longtitude
  const defaultGpsLatitude = hasCoordinate(rawDefaultGpsLatitude) ? rawDefaultGpsLatitude : null
  const defaultGpsLongtitude = hasCoordinate(rawDefaultGpsLongtitude) ? rawDefaultGpsLongtitude : null

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) {
      return
    }

    const map = L.map(mapElementRef.current, {
      zoomControl: true,
      attributionControl: true,
      doubleClickZoom: false,
    }).setView([12.658763, 101.310768], DEFAULT_GPS_MAP_ZOOM)

    const layer = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: '&copy; OpenStreetMap contributors &copy; Esri',
        maxZoom: 19,
      },
    )
    layer.addTo(map)

    mapRef.current = map
    layerRef.current = layer

    return () => {
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (!map || !layer) {
      return
    }

    const nextUrl =
      mapViewMode === 'street'
        ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

    layer.setUrl(nextUrl)
  }, [mapViewMode])

  useEffect(() => {
    const map = mapRef.current
    if (!map) {
      return
    }

    overlaysRef.current.forEach((layer) => layer.remove())
    overlaysRef.current = []

    const bounds: L.LatLngBounds[] = []
    records.forEach((record) => {
      const workArea = record.detail?.workArea
      if (!workArea || workArea.mapMode?.toLowerCase() !== 'gps') {
        return
      }

      const tone = getPermitTypeTone(record.permit.permitType)
      const referenceLatitude = hasCoordinate(workArea.gpsLatitude)
        ? workArea.gpsLatitude
        : defaultGpsLatitude
      const referenceLongitude = hasCoordinate(workArea.gpsLongtitude)
        ? workArea.gpsLongtitude
        : defaultGpsLongtitude

      if (Array.isArray(workArea.gpsAreas)) {
        workArea.gpsAreas
          .filter(isGpsArea)
          .map(normalizeGpsArea)
          .filter((area) => isReasonableGpsArea(area))
          .filter((area) => isGpsAreaNearReference(area, referenceLatitude, referenceLongitude))
          .forEach((area) => {
          const rectangle = L.rectangle(
            [
              [area.south, area.west],
              [area.north, area.east],
            ],
            {
              color: tone.stroke,
              weight: 2,
              fillColor: tone.stroke,
              fillOpacity: 0.15,
            },
          )
          rectangle.on('click', () => onSelect(record))
          rectangle.bindTooltip(record.permit.permitNumber, {
            permanent: false,
            direction: 'top',
          })
          rectangle.addTo(map)
          overlaysRef.current.push(rectangle)
          bounds.push(
            L.latLngBounds(
              [area.south, area.west],
              [area.north, area.east],
            ),
          )
          })
      }
    })

    if (bounds.length === 0 && defaultGpsLatitude !== null && defaultGpsLongtitude !== null) {
      map.setView([defaultGpsLatitude, defaultGpsLongtitude], DEFAULT_GPS_MAP_ZOOM, { animate: false })
      return
    }

    if (bounds.length > 0) {
      const merged = L.latLngBounds(bounds[0].getSouthWest(), bounds[0].getNorthEast())
      bounds.slice(1).forEach((entry) => merged.extend(entry))
      if (Math.abs(merged.getNorth() - merged.getSouth()) < 0.00001 && Math.abs(merged.getEast() - merged.getWest()) < 0.00001) {
        map.setView(merged.getCenter(), DEFAULT_GPS_MAP_ZOOM, { animate: false })
      } else {
        map.fitBounds(merged.pad(0.15))
      }
    }
  }, [defaultGpsLatitude, defaultGpsLongtitude, onSelect, records])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant={mapViewMode === 'street' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMapViewMode('street')}
        >
          Street
        </Button>
        <Button
          type="button"
          variant={mapViewMode === 'satellite' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMapViewMode('satellite')}
        >
          Satellite
        </Button>
      </div>
      <div ref={mapElementRef} className="h-[720px] overflow-hidden rounded-2xl border border-slate-200" />
      {records.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600">
          ไม่พบ Permit ที่มีการ Mark Layout แบบ GPS ตามเงื่อนไขที่ค้นหา
        </div>
      ) : null}
    </div>
  )
}

export function OverallDashboardPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as LayoutState | undefined) ?? {}
  const currentUser = useMemo(() => (state.user ?? {}) as any, [state.user])
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])
  const tenantId = resolveTenantId(state.site?.id, persistedSiteId)
  const siteId = resolveTenantCandidate(state.site?.id, persistedSiteId)

  const [filters, setFilters] = useState(createDefaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters)
  const [permits, setPermits] = useState<PermitListItem[]>([])
  const [detailMap, setDetailMap] = useState<Map<string, PermitDetail | null>>(new Map())
  const [companies, setCompanies] = useState<ContractorCompany[]>([])
  const [workAreas, setWorkAreas] = useState<WorkAreaOption[]>([])
  const [workAreaSearchTerm, setWorkAreaSearchTerm] = useState('')
  const [permitTypes, setPermitTypes] = useState<PermitType[]>([])
  const [subactivityOptions, setSubactivityOptions] = useState<SubactivityOption[]>([])
  const [subactivitySearchTerm, setSubactivitySearchTerm] = useState('')
  const [selectedAreaPlotPlans, setSelectedAreaPlotPlans] = useState<PlotPlanDto[]>([])
  const [defaultGps, setDefaultGps] = useState<PlotPlanGps | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [dashboardSummary, setDashboardSummary] = useState<OverallDashboardSummary>(EMPTY_DASHBOARD_SUMMARY)
  const [isLoading, setIsLoading] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const [isLoadingCounts, setIsLoadingCounts] = useState(false)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [isLoadingPlotPlans, setIsLoadingPlotPlans] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selectedRecord, setSelectedRecord] = useState<DashboardPermitRecord | null>(null)
  const [isWorkerDialogOpen, setIsWorkerDialogOpen] = useState(false)
  const [workerRows, setWorkerRows] = useState<OverallDashboardWorkerItem[]>([])
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(false)
  const [isExportingWorkers, setIsExportingWorkers] = useState(false)

  const currentUserType = normalizeUserType(
    currentUser.userType ?? currentUser.user_type ?? currentUser.type,
  )
  const isContractorUser = currentUserType === 'CONTRACTOR'
  const requiresWorkAreaForLayout = filters.showLayout && filters.workAreaIds.length !== 1
  const appliedRequiresWorkAreaForLayout =
    appliedFilters.showLayout && appliedFilters.workAreaIds.length !== 1

  const contractorCompanyId = useMemo(() => {
    const candidates: Array<string | null | undefined> = [
      currentUser.companyId as string | undefined,
      currentUser.company_id as string | undefined,
      currentUser.company?.companyId as string | undefined,
      currentUser.company?.id as string | undefined,
    ]
    for (const candidate of candidates) {
      const normalized = normalizeGuid(candidate)
      if (normalized) {
        return normalized
      }
    }
    return null
  }, [currentUser])

  useEffect(() => {
    const today = getTodayDateValue()

    setFilters((prev) => {
      if (prev.dateFrom && prev.dateTo) {
        return prev
      }
      return {
        ...prev,
        dateFrom: prev.dateFrom || today,
        dateTo: prev.dateTo || today,
      }
    })

    setAppliedFilters((prev) => {
      if (prev.dateFrom && prev.dateTo) {
        return prev
      }
      return {
        ...prev,
        dateFrom: prev.dateFrom || today,
        dateTo: prev.dateTo || today,
      }
    })
  }, [])

  const effectiveCompanyId =
    isContractorUser && contractorCompanyId ? contractorCompanyId : appliedFilters.companyId !== 'ALL' ? appliedFilters.companyId : null

  const singleWorkAreaId = appliedFilters.workAreaIds.length === 1 ? appliedFilters.workAreaIds[0] : null
  const selectedWorkArea = useMemo(
    () => workAreas.find((item) => item.locationId === singleWorkAreaId) ?? null,
    [singleWorkAreaId, workAreas],
  )

  const filteredWorkAreas = useMemo(() => {
    const term = workAreaSearchTerm.trim().toLowerCase()
    if (!term) {
      return workAreas
    }
    return workAreas.filter((item) => {
      const name = item.name?.toLowerCase() ?? ''
      const code = item.code?.toLowerCase() ?? ''
      return name.includes(term) || code.includes(term)
    })
  }, [workAreaSearchTerm, workAreas])

  const filteredSubactivityOptions = useMemo(() => {
    const term = subactivitySearchTerm.trim().toLowerCase()
    if (!term) {
      return subactivityOptions
    }
    return subactivityOptions.filter((item) => {
      const name = item.name?.toLowerCase() ?? ''
      const code = item.code?.toLowerCase() ?? ''
      return name.includes(term) || code.includes(term)
    })
  }, [subactivitySearchTerm, subactivityOptions])

  const selectedPlotPlanImageUrl = useMemo(() => {
    const selectedPlan = selectedAreaPlotPlans[0]
    if (selectedPlan?.backgroundPath) {
      return resolveAssetUrl(selectedPlan.backgroundPath)
    }
    return ''
  }, [selectedAreaPlotPlans])

  const permitRecords = useMemo<DashboardPermitRecord[]>(
    () => permits.map((permit) => ({ permit, detail: detailMap.get(permit.permitId) ?? null })),
    [detailMap, permits],
  )

  const plotPlanRecords = useMemo(() => {
    return permitRecords.filter((record) => {
      const workArea = record.detail?.workArea
      if (!workArea || workArea.mapMode?.toLowerCase() === 'gps') {
        return false
      }
      const hasArea =
        (Array.isArray(workArea.areas) && workArea.areas.some(isPlotArea)) ||
        isPlotArea(workArea.area) ||
        Boolean(workArea.marker)
      return hasArea
    })
  }, [permitRecords])

  const gpsRecords = useMemo(() => {
    return permitRecords.filter((record) => {
      const workArea = record.detail?.workArea
      if (!workArea || workArea.mapMode?.toLowerCase() !== 'gps') {
        return false
      }
      return Array.isArray(workArea.gpsAreas) && workArea.gpsAreas.some(isGpsArea)
    })
  }, [permitRecords])

  const statusSummary = useMemo(() => {
    return {
      total: dashboardSummary.totalPermit ?? 0,
      waitOpen: dashboardSummary.waitForOpen ?? 0,
      opened: dashboardSummary.opened ?? 0,
      closed: dashboardSummary.closed ?? 0,
      cancelled: dashboardSummary.cancelled ?? 0,
      workers: dashboardSummary.totalWorker ?? 0,
    }
  }, [dashboardSummary])

  const permitTypeSummary = useMemo(() => {
    return {
      cold: dashboardSummary.coldWork ?? 0,
      confine: dashboardSummary.confineWork ?? 0,
      hot: dashboardSummary.hotWork ?? 0,
      confineInertGasArea: dashboardSummary.confineWorkInertGasArea ?? 0,
      confineNonInertGasArea: dashboardSummary.confineWorkNonInertGasArea ?? 0,
      hotOpenFire: dashboardSummary.hotWorkOpenFire ?? 0,
      hotNonOpenFire: dashboardSummary.hotWorkNonOpenFire ?? 0,
    }
  }, [dashboardSummary])

  const loadOptions = useCallback(async () => {
    const [companyResponse, workAreaResponse, permitTypeResponse, subactivityResponse] = await Promise.all([
      getContractorCompanies({ tenantId, pageNumber: 1, pageSize: 200 }).catch(() => ({
        items: [],
        pageNumber: 1,
        pageSize: 200,
        totalItems: 0,
        totalPages: 0,
      })),
      fetchWorkAreas(tenantId).catch(() => []),
      getAvailablePermitTypes(tenantId).catch(() => []),
      getSubactivityOptions().catch(() => []),
    ])

    const allowedPermitTypeCodes: string[] = Object.values(CARD_PERMIT_TYPE_CODES)
    setCompanies(companyResponse.items ?? [])
    setWorkAreas(workAreaResponse ?? [])
    setPermitTypes((permitTypeResponse ?? []).filter((type) => allowedPermitTypeCodes.includes(type.permitCode)))
    setSubactivityOptions(subactivityResponse ?? [])
  }, [tenantId])

  useEffect(() => {
    if (!state.user) {
      navigate('/modules', { replace: true })
      return
    }
    void loadOptions()
  }, [loadOptions, navigate, state.user])

  const loadDashboard = useCallback(async () => {
    if (appliedRequiresWorkAreaForLayout) {
      setPermits([])
      setDetailMap(new Map())
      setTotalItems(0)
      setTotalPages(1)
      setErrorMessage('กรุณาเลือกพื้นที่ปฏิบัติงานก่อนค้นหาข้อมูลแบบ Layout')
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await getOverallDashboardPermits({
        tenantId,
        siteId: siteId ?? undefined,
        companyIds: effectiveCompanyId ? [effectiveCompanyId] : undefined,
        locationIds: appliedFilters.workAreaIds.length > 0 ? appliedFilters.workAreaIds : undefined,
        permitType: appliedFilters.permitType !== 'ALL' ? appliedFilters.permitType : undefined,
        searchTerm: appliedFilters.keyword.trim() || undefined,
        plannedStartFrom: toDateFilterStart(appliedFilters.dateFrom),
        plannedStartTo: toDateFilterEnd(appliedFilters.dateTo),
        offHoursOnly: appliedFilters.offHoursOnly,
        projectNo: appliedFilters.projectNo !== 'ALL' ? appliedFilters.projectNo : undefined,
        workOrderNo: appliedFilters.workOrderNo !== 'ALL' ? appliedFilters.workOrderNo : undefined,
        statuses: appliedFilters.statusCategory !== 'ALL' ? [appliedFilters.statusCategory] : STATUS_SCOPE,
        inertGasArea: appliedFilters.inertGasArea !== 'ALL' ? appliedFilters.inertGasArea : undefined,
        toolType: appliedFilters.toolType !== 'ALL' ? appliedFilters.toolType : undefined,
        subactivityCodes: appliedFilters.subactivityCodes.length > 0 ? appliedFilters.subactivityCodes : undefined,
        pageNumber,
        pageSize,
      })

      setPermits(response.items ?? [])
      setTotalItems(response.totalItems ?? response.items?.length ?? 0)
      setTotalPages(Math.max(response.totalPages ?? 1, 1))
    } catch (error) {
      setPermits([])
      setDetailMap(new Map())
      setTotalItems(0)
      setTotalPages(1)
      setErrorMessage('ไม่สามารถโหลดข้อมูล Overall Dashboard ได้')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.inertGasArea,
    appliedFilters.keyword,
    appliedFilters.offHoursOnly,
    appliedFilters.permitType,
    appliedFilters.projectNo,
    appliedFilters.workOrderNo,
    appliedFilters.statusCategory,
    appliedFilters.subactivityCodes,
    appliedFilters.toolType,
    appliedFilters.workAreaIds,
    effectiveCompanyId,
    pageNumber,
    pageSize,
    siteId,
    tenantId,
    appliedRequiresWorkAreaForLayout,
  ])

  useEffect(() => {
    void loadDashboard()
  }, [loadDashboard])

  const loadCounts = useCallback(async () => {
    if (appliedRequiresWorkAreaForLayout) {
      setDashboardSummary(EMPTY_DASHBOARD_SUMMARY)
      return
    }

    setIsLoadingCounts(true)
    try {
      const baseQuery = {
        tenantId,
        siteId: siteId ?? undefined,
        companyIds: effectiveCompanyId ? [effectiveCompanyId] : undefined,
        locationIds: appliedFilters.workAreaIds.length > 0 ? appliedFilters.workAreaIds : undefined,
        searchTerm: appliedFilters.keyword.trim() || undefined,
        plannedStartFrom: toDateFilterStart(appliedFilters.dateFrom),
        plannedStartTo: toDateFilterEnd(appliedFilters.dateTo),
        offHoursOnly: appliedFilters.offHoursOnly,
        projectNo: appliedFilters.projectNo !== 'ALL' ? appliedFilters.projectNo : undefined,
        workOrderNo: appliedFilters.workOrderNo !== 'ALL' ? appliedFilters.workOrderNo : undefined,
        subactivityCodes: appliedFilters.subactivityCodes.length > 0 ? appliedFilters.subactivityCodes : undefined,
        statuses: STATUS_SCOPE,
      }

      const summary = await getOverallDashboardSummary(baseQuery)
      setDashboardSummary(summary)
    } catch (error) {
      console.error(error)
      setDashboardSummary(EMPTY_DASHBOARD_SUMMARY)
    } finally {
      setIsLoadingCounts(false)
    }
  }, [
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.keyword,
    appliedFilters.projectNo,
    appliedFilters.workOrderNo,
    appliedFilters.subactivityCodes,
    appliedFilters.workAreaIds,
    appliedFilters.offHoursOnly,
    effectiveCompanyId,
    siteId,
    tenantId,
    appliedRequiresWorkAreaForLayout,
  ])

  useEffect(() => {
    if (permitTypes.length === 0) {
      return
    }
    void loadCounts()
  }, [loadCounts, permitTypes.length])

  useEffect(() => {
    if (!appliedFilters.showLayout || permits.length === 0) {
      setDetailMap(new Map())
      setIsLoadingDetails(false)
      return
    }

    let cancelled = false
    setIsLoadingDetails(true)

    loadPermitDetails(tenantId, permits)
      .then((map) => {
        if (!cancelled) {
          setDetailMap(map)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingDetails(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [appliedFilters.showLayout, permits, tenantId])

  useEffect(() => {
    if (!appliedFilters.showLayout || !singleWorkAreaId) {
      setSelectedAreaPlotPlans([])
      setDefaultGps(null)
      if (appliedFilters.layoutMode !== 'plot-plan') {
        setIsLoadingPlotPlans(false)
      }
      return
    }

    if (appliedFilters.layoutMode !== 'plot-plan') {
      setSelectedAreaPlotPlans([])
      return
    }

    let cancelled = false
    setIsLoadingPlotPlans(true)

    getPlotPlans(singleWorkAreaId)
      .then((items) => {
        if (!cancelled) {
          setSelectedAreaPlotPlans(
            (items ?? []).map((item) => ({
              plotPlanId: item.plotPlanId,
              locationId: singleWorkAreaId,
              workAreaCode: selectedWorkArea?.code ?? '',
              workAreaName: selectedWorkArea?.name ?? '',
              name: item.name,
              backgroundPath: item.backgroundPath ?? null,
              isActive: true,
            })),
          )
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedAreaPlotPlans([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingPlotPlans(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [appliedFilters.layoutMode, appliedFilters.showLayout, singleWorkAreaId, selectedWorkArea])

  useEffect(() => {
    if (!appliedFilters.showLayout || appliedFilters.layoutMode !== 'map' || !singleWorkAreaId) {
      setDefaultGps(null)
      return
    }

    let cancelled = false

    getDefaultGps(singleWorkAreaId)
      .then((gps) => {
        if (!cancelled) {
          setDefaultGps(gps)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDefaultGps(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [appliedFilters.layoutMode, appliedFilters.showLayout, singleWorkAreaId])

  const handleSearch = () => {
    setPageNumber(1)
    setSelectedRecord(null)
    setAppliedFilters({
      ...filters,
      companyId: isContractorUser && contractorCompanyId ? contractorCompanyId : filters.companyId,
    })
  }

  const applyCardFilter = (dimension: 'permitType' | 'statusCategory', value: string) => {
    setPageNumber(1)
    setSelectedRecord(null)
    setFilters((prev) => {
      const nextValue = prev[dimension] === value ? 'ALL' : value
      const next: DashboardFilters = { ...prev, [dimension]: nextValue }
      if (dimension === 'permitType') {
        next.inertGasArea = 'ALL'
        next.toolType = 'ALL'
      }
      setAppliedFilters(next)
      return next
    })
  }

  const applySubCardFilter = (permitType: string, dimension: 'inertGasArea' | 'toolType', value: string) => {
    setPageNumber(1)
    setSelectedRecord(null)
    setFilters((prev) => {
      const isActive = prev.permitType === permitType && prev[dimension] === value
      const next: DashboardFilters = isActive
        ? { ...prev, permitType: 'ALL', inertGasArea: 'ALL', toolType: 'ALL' }
        : {
            ...prev,
            permitType,
            inertGasArea: dimension === 'inertGasArea' ? value : 'ALL',
            toolType: dimension === 'toolType' ? value : 'ALL',
          }
      setAppliedFilters(next)
      return next
    })
  }

  const handleTotalPermitCardClick = () => {
    setPageNumber(1)
    setSelectedRecord(null)
    setFilters((prev) => {
      const next: DashboardFilters = {
        ...prev,
        permitType: 'ALL',
        statusCategory: 'ALL',
        inertGasArea: 'ALL',
        toolType: 'ALL',
      }
      setAppliedFilters(next)
      return next
    })
  }

  const handleOpenWorkerDialog = async () => {
    setIsWorkerDialogOpen(true)
    setIsLoadingWorkers(true)
    try {
      const workers = await getOverallDashboardWorkers({
        tenantId,
        siteId: siteId ?? undefined,
        companyIds: effectiveCompanyId ? [effectiveCompanyId] : undefined,
        locationIds: appliedFilters.workAreaIds.length > 0 ? appliedFilters.workAreaIds : undefined,
        permitType: appliedFilters.permitType !== 'ALL' ? appliedFilters.permitType : undefined,
        searchTerm: appliedFilters.keyword.trim() || undefined,
        plannedStartFrom: toDateFilterStart(appliedFilters.dateFrom),
        plannedStartTo: toDateFilterEnd(appliedFilters.dateTo),
        offHoursOnly: appliedFilters.offHoursOnly,
        projectNo: appliedFilters.projectNo !== 'ALL' ? appliedFilters.projectNo : undefined,
        workOrderNo: appliedFilters.workOrderNo !== 'ALL' ? appliedFilters.workOrderNo : undefined,
        statuses: appliedFilters.statusCategory !== 'ALL' ? [appliedFilters.statusCategory] : STATUS_SCOPE,
        inertGasArea: appliedFilters.inertGasArea !== 'ALL' ? appliedFilters.inertGasArea : undefined,
        toolType: appliedFilters.toolType !== 'ALL' ? appliedFilters.toolType : undefined,
        subactivityCodes: appliedFilters.subactivityCodes.length > 0 ? appliedFilters.subactivityCodes : undefined,
      })
      setWorkerRows(workers)
    } catch (error) {
      console.error(error)
      setWorkerRows([])
    } finally {
      setIsLoadingWorkers(false)
    }
  }

  const handleExportWorkersExcel = async () => {
    if (workerRows.length === 0) {
      toast.error('ไม่มีข้อมูลผู้ปฏิบัติงานให้ export')
      return
    }

    setIsExportingWorkers(true)
    try {
      const exportRows = workerRows.map((worker) => ({
        ชื่อ: worker.workerName || '-',
        บริษัท: worker.workerCompany || '-',
        หน้าที่ความรับผิดชอบ: worker.workerRole || '-',
        พื้นที่: worker.area || '-',
        'Permit No': worker.permitNumber,
        สถานะ: getPermitStatusLabel(worker.status, worker.statusName),
      }))

      const xlsx = await import('xlsx')
      const worksheet = xlsx.utils.json_to_sheet(exportRows)
      const workbook = xlsx.utils.book_new()
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Workers')
      const dateStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      xlsx.writeFile(workbook, `worker-list-${dateStamp}.xlsx`)
      toast.success(`Export สำเร็จ (${workerRows.length} รายการ)`)
    } catch (error) {
      console.error(error)
      toast.error('Export Excel ไม่สำเร็จ')
    } finally {
      setIsExportingWorkers(false)
    }
  }

  const handleClear = () => {
    const next = createDefaultFilters()
    if (isContractorUser && contractorCompanyId) {
      next.companyId = contractorCompanyId
    }
    setFilters(next)
    setAppliedFilters(next)
    setPageSize(DEFAULT_PAGE_SIZE)
    setPageNumber(1)
    setSelectedRecord(null)
  }

  const handlePageChange = (nextPage: number) => {
    if (nextPage >= 1 && nextPage <= totalPages) {
      setPageNumber(nextPage)
    }
  }

  const handlePageSizeChange = (value: string) => {
    const nextSize = Number(value) || DEFAULT_PAGE_SIZE
    setPageSize(nextSize)
    setPageNumber(1)
  }

  useEffect(() => {
    if (isContractorUser && contractorCompanyId) {
      setFilters((prev) => ({ ...prev, companyId: contractorCompanyId }))
      setAppliedFilters((prev) => ({ ...prev, companyId: contractorCompanyId }))
    }
  }, [contractorCompanyId, isContractorUser])

  const selectedCompanyLabel = useMemo(() => {
    const found = companies.find((item) => item.companyId === effectiveCompanyId)
    return found?.companyName ?? '-'
  }, [companies, effectiveCompanyId])

  const startIndex = totalItems === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const endIndex = Math.min(pageNumber * pageSize, totalItems)

  const renderPaginationBar = () => (
    <Card className="border-amber-200 p-3">
      <CardContent className="flex flex-col gap-4 p-0 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-600">
          แสดง {startIndex === 0 ? 0 : startIndex} - {endIndex} จากทั้งหมด {totalItems} รายการ
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>จำนวนต่อหน้า</span>
            <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="h-9 w-24 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={size}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pageNumber <= 1 || isLoading}
              onClick={() => handlePageChange(pageNumber - 1)}
            >
              ก่อนหน้า
            </Button>
            <span className="text-sm text-gray-600">
              หน้า {pageNumber} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pageNumber >= totalPages || isLoading}
              onClick={() => handlePageChange(pageNumber + 1)}
            >
              ถัดไป
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  const workAreaLabelMap = useMemo(() => {
    return workAreas.reduce<Record<string, string>>((acc, item) => {
      const label = buildLocationDescription(item)
      if (item.locationId) {
        acc[item.locationId] = label
      }
      if (item.code) {
        acc[item.code] = label
      }
      if (item.name) {
        acc[item.name] = label
      }
      return acc
    }, {})
  }, [workAreas])

  const resolvePermitLocationLabel = useCallback(
    (permit: PermitListItem) => {
      const candidates = [
        permit.locationName,
        permit.location,
        permit.unitName,
        permit.unit,
      ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)

      for (const candidate of candidates) {
        const mapped = workAreaLabelMap[candidate]
        if (mapped) {
          return mapped.trim()
        }
      }

      for (const candidate of candidates) {
        if (!GUID_REGEX.test(candidate.trim())) {
          return candidate.trim()
        }
      }

      return '-'
    },
    [workAreaLabelMap],
  )

  const handleExportExcel = useCallback(async () => {
    setIsExportingExcel(true)
    try {
      // getOverallDashboardPermits' backend caps pageSize at 200 regardless of what's
      // requested, so a single oversized-pageSize call silently truncates once there are
      // more than 200 matching permits. Page through everything instead.
      const pageSizeCap = 200
      let rows: PermitListItem[] = []
      let pageNumber = 1
      while (true) {
        const response = await getOverallDashboardPermits({
          tenantId,
          siteId: siteId ?? undefined,
          companyIds: effectiveCompanyId ? [effectiveCompanyId] : undefined,
          locationIds: appliedFilters.workAreaIds.length > 0 ? appliedFilters.workAreaIds : undefined,
          permitType: appliedFilters.permitType !== 'ALL' ? appliedFilters.permitType : undefined,
          searchTerm: appliedFilters.keyword.trim() || undefined,
          plannedStartFrom: toDateFilterStart(appliedFilters.dateFrom),
          plannedStartTo: toDateFilterEnd(appliedFilters.dateTo),
          offHoursOnly: appliedFilters.offHoursOnly,
          projectNo: appliedFilters.projectNo !== 'ALL' ? appliedFilters.projectNo : undefined,
          workOrderNo: appliedFilters.workOrderNo !== 'ALL' ? appliedFilters.workOrderNo : undefined,
          statuses: appliedFilters.statusCategory !== 'ALL' ? [appliedFilters.statusCategory] : STATUS_SCOPE,
          inertGasArea: appliedFilters.inertGasArea !== 'ALL' ? appliedFilters.inertGasArea : undefined,
          toolType: appliedFilters.toolType !== 'ALL' ? appliedFilters.toolType : undefined,
          subactivityCodes: appliedFilters.subactivityCodes.length > 0 ? appliedFilters.subactivityCodes : undefined,
          pageNumber,
          pageSize: pageSizeCap,
        })

        const items = response.items ?? []
        rows = rows.concat(items)
        if (items.length < pageSizeCap || rows.length >= (response.totalItems ?? 0)) {
          break
        }
        pageNumber += 1
      }

      if (rows.length === 0) {
        toast.error('ไม่มีข้อมูลให้ export ตามเงื่อนไขที่เลือก')
        return
      }

      const exportRows = rows.map((permit) => ({
        'Permit No': permit.permitNumber,
        'สร้างเมื่อ': formatWorkDate(permit.createdAt),
        'ประเภท': permit.permitType || '-',
        'รายละเอียดงาน': permit.workDescription || permit.title || '-',
        'พื้นที่ปฏิบัติงาน': resolvePermitLocationLabel(permit),
        'ผู้ขอ': permit.requesterName || '-',
        'บริษัทผู้ขอ': permit.requesterCompany || '-',
        'วันที่ปฏิบัติงาน': formatWorkPeriod(permit.plannedStart, permit.plannedEnd),
        'สถานะ': getPermitStatusLabel(permit.status, permit.statusName),
      }))

      const xlsx = await import('xlsx')
      const worksheet = xlsx.utils.json_to_sheet(exportRows)
      const workbook = xlsx.utils.book_new()
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Overall Dashboard')
      const dateStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      xlsx.writeFile(workbook, `overall-dashboard-${dateStamp}.xlsx`)
      toast.success(`Export สำเร็จ (${rows.length} รายการ)`)
    } catch (error) {
      console.error(error)
      toast.error('Export Excel ไม่สำเร็จ')
    } finally {
      setIsExportingExcel(false)
    }
  }, [
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.inertGasArea,
    appliedFilters.keyword,
    appliedFilters.offHoursOnly,
    appliedFilters.permitType,
    appliedFilters.projectNo,
    appliedFilters.workOrderNo,
    appliedFilters.statusCategory,
    appliedFilters.subactivityCodes,
    appliedFilters.toolType,
    appliedFilters.workAreaIds,
    effectiveCompanyId,
    resolvePermitLocationLabel,
    siteId,
    tenantId,
  ])

  const layoutPanelContent = useMemo(() => {
    if (!appliedFilters.showLayout) {
      return null
    }

    if (!singleWorkAreaId) {
      return (
        <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 px-4 py-6 text-sm text-amber-800">
          เลือกพื้นที่ปฏิบัติงาน 1 พื้นที่ก่อน จึงจะแสดง Layout ได้
        </div>
      )
    }

    if (appliedFilters.layoutMode === 'plot-plan') {
      if (isLoadingPlotPlans || isLoadingDetails) {
        return (
          <div className="flex min-h-[220px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-slate-500" />
            <span className="text-sm text-slate-600">กำลังโหลด Plot Plan และ Layout</span>
          </div>
        )
      }

      if (!selectedPlotPlanImageUrl) {
        return (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600">
            พื้นที่นี้ไม่มีข้อมูล Plot Plan ที่พร้อมแสดงผล
          </div>
        )
      }

      if (plotPlanRecords.length === 0) {
        return (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600">
            ไม่พบ Permit ที่มีการ Mark Layout แบบ Plot Plan ตามเงื่อนไขที่ค้นหา
          </div>
        )
      }

      return (
        <PlotPlanLayoutView
          imageUrl={selectedPlotPlanImageUrl}
          records={plotPlanRecords}
          onSelect={setSelectedRecord}
        />
      )
    }

    if (isLoadingDetails) {
      return (
        <div className="flex min-h-[220px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
          <Loader2 className="mr-2 h-5 w-5 animate-spin text-slate-500" />
          <span className="text-sm text-slate-600">กำลังโหลด GPS Layout</span>
        </div>
      )
    }

    return (
        <GpsLayoutView
          records={gpsRecords}
          defaultGps={defaultGps}
          onSelect={setSelectedRecord}
        />
    )
  }, [
    defaultGps,
    appliedFilters.layoutMode,
    appliedFilters.showLayout,
    singleWorkAreaId,
    gpsRecords,
    isLoadingDetails,
    isLoadingPlotPlans,
    plotPlanRecords,
    selectedPlotPlanImageUrl,
  ])

  return (
    <div className="space-y-6">
      <PageTitle
        currentPage="overall-dashboard"
        onNavigate={(page) => {
          if (page === 'home') {
            navigate('/dashboard', { state })
          }
        }}
      />

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
            <Search className="h-5 w-5 text-sky-600" />
            ตัวกรองข้อมูล
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 xl:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="overall-keyword">ค้นหา Permit No. หรือรายละเอียดงาน</Label>
              <Input
                id="overall-keyword"
                value={filters.keyword}
                onChange={(event) => setFilters((prev) => ({ ...prev, keyword: event.target.value }))}
                placeholder="เช่น Permit No. หรือรายละเอียดงาน"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="overall-date-from">วันที่เริ่มต้น</Label>
              <DatePicker
                id="overall-date-from"
                value={filters.dateFrom}
                onChange={(value) => setFilters((prev) => ({ ...prev, dateFrom: value }))}
                placeholder="เลือกวันที่"
                allowPastDates
                hideMaxDateHint
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="overall-date-to">วันที่สิ้นสุด</Label>
              <DatePicker
                id="overall-date-to"
                value={filters.dateTo}
                onChange={(value) => setFilters((prev) => ({ ...prev, dateTo: value }))}
                placeholder="เลือกวันที่"
                allowPastDates
                hideMaxDateHint
              />
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="overall-project-no">Project No.</Label>
              <AsyncSearchableSelect
                value={filters.projectNo}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, projectNo: value }))}
                fetchOptions={getProjectNoOptions}
                placeholder="เลือก Project No."
                searchPlaceholder="พิมพ์เลข Project No. หรือรายละเอียด"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="overall-work-order-no">Work Order No.</Label>
              <AsyncSearchableSelect
                value={filters.workOrderNo}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, workOrderNo: value }))}
                fetchOptions={getWorkOrderNoOptions}
                placeholder="เลือก Work Order No."
                searchPlaceholder="พิมพ์เลข Work Order No. หรือรายละเอียด"
              />
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="space-y-2">
              <Label>บริษัท</Label>
              <Select
                value={filters.companyId}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, companyId: value }))}
                disabled={isContractorUser && Boolean(contractorCompanyId)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="เลือกบริษัท" />
                </SelectTrigger>
                <SelectContent>
                  {!isContractorUser ? <SelectItem value="ALL">ทั้งหมด</SelectItem> : null}
                  {companies.map((company) => (
                    <SelectItem key={company.companyId} value={company.companyId}>
                      {company.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>พื้นที่ปฏิบัติงาน</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between bg-white font-normal">
                    <span className="truncate">
                      {filters.workAreaIds.length === 0
                        ? 'ทั้งหมด'
                        : filters.workAreaIds.length === 1
                          ? buildLocationDescription(
                              workAreas.find((area) => area.locationId === filters.workAreaIds[0]) ?? null,
                            )
                          : `เลือกแล้ว ${filters.workAreaIds.length} พื้นที่`}
                    </span>
                    <span className="text-xs text-slate-500">
                      {filters.workAreaIds.length === 0 ? 'ทั้งหมด' : `${filters.workAreaIds.length} พื้นที่`}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-72 max-h-80 overflow-y-auto">
                  <div className="px-3 py-2">
                    <Input
                      placeholder="ค้นหาพื้นที่..."
                      value={workAreaSearchTerm}
                      onChange={(event) => setWorkAreaSearchTerm(event.target.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      className="h-8"
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={filters.workAreaIds.length === 0}
                    onCheckedChange={() => setFilters((prev) => ({ ...prev, workAreaIds: [] }))}
                    onSelect={(event) => event.preventDefault()}
                  >
                    ทั้งหมด
                  </DropdownMenuCheckboxItem>
                  {filteredWorkAreas.length === 0 ? (
                    <DropdownMenuCheckboxItem disabled checked={false}>
                      ไม่พบพื้นที่ปฏิบัติงาน
                    </DropdownMenuCheckboxItem>
                  ) : null}
                  {filteredWorkAreas.map((area) => (
                    <DropdownMenuCheckboxItem
                      key={area.locationId}
                      checked={filters.workAreaIds.includes(area.locationId)}
                      onCheckedChange={(checked) =>
                        setFilters((prev) => ({
                          ...prev,
                          workAreaIds:
                            checked === true
                              ? [...prev.workAreaIds, area.locationId]
                              : prev.workAreaIds.filter((id) => id !== area.locationId),
                        }))
                      }
                      onSelect={(event) => event.preventDefault()}
                    >
                      {buildLocationDescription(area)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {filters.showLayout && filters.workAreaIds.length !== 1 ? (
                <p className="text-xs text-amber-600">เลือกพื้นที่ปฏิบัติงาน 1 พื้นที่ จึงจะค้นหาและแสดง Layout ได้</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>ประเภท Permit</Label>
              <Select
                value={filters.permitType}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, permitType: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="เลือกประเภท Permit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">ทั้งหมด</SelectItem>
                  {permitTypes.map((type) => (
                    <SelectItem key={type.permitCode} value={type.permitCode}>
                      {type.permitType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="space-y-2">
              <Label>ประเภทงานที่เกี่ยวข้อง</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between bg-white font-normal">
                    <span className="truncate">
                      {filters.subactivityCodes.length === 0
                        ? 'ทั้งหมด'
                        : filters.subactivityCodes.length === 1
                          ? (subactivityOptions.find((option) => option.code === filters.subactivityCodes[0])
                              ?.name ?? filters.subactivityCodes[0])
                          : `เลือกแล้ว ${filters.subactivityCodes.length} ประเภท`}
                    </span>
                    <span className="text-xs text-slate-500">
                      {filters.subactivityCodes.length === 0 ? 'ทั้งหมด' : `${filters.subactivityCodes.length} ประเภท`}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-72 max-h-80 overflow-y-auto">
                  <div className="px-3 py-2">
                    <Input
                      placeholder="ค้นหาประเภทงาน..."
                      value={subactivitySearchTerm}
                      onChange={(event) => setSubactivitySearchTerm(event.target.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      className="h-8"
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={filters.subactivityCodes.length === 0}
                    onCheckedChange={() => setFilters((prev) => ({ ...prev, subactivityCodes: [] }))}
                    onSelect={(event) => event.preventDefault()}
                  >
                    ทั้งหมด
                  </DropdownMenuCheckboxItem>
                  {filteredSubactivityOptions.length === 0 ? (
                    <DropdownMenuCheckboxItem disabled checked={false}>
                      ไม่พบประเภทงาน
                    </DropdownMenuCheckboxItem>
                  ) : null}
                  {filteredSubactivityOptions.map((option) => (
                    <DropdownMenuCheckboxItem
                      key={option.code}
                      checked={filters.subactivityCodes.includes(option.code)}
                      onCheckedChange={(checked) =>
                        setFilters((prev) => ({
                          ...prev,
                          subactivityCodes:
                            checked === true
                              ? [...prev.subactivityCodes, option.code]
                              : prev.subactivityCodes.filter((code) => code !== option.code),
                        }))
                      }
                      onSelect={(event) => event.preventDefault()}
                    >
                      {option.name} ({option.code})
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={filters.showLayout}
                  disabled={filters.workAreaIds.length !== 1}
                  onCheckedChange={(checked) => setFilters((prev) => ({ ...prev, showLayout: checked }))}
                />
                <Label>แสดง Layout แผนที่</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={filters.offHoursOnly}
                  onCheckedChange={(checked) => setFilters((prev) => ({ ...prev, offHoursOnly: checked }))}
                />
                <Label>แสดงเฉพาะงานนอกเวลาปฏิบัติงานปกติ (หลัง 17:00 น.)</Label>
              </div>

              {filters.showLayout ? (
                <div className="flex items-center gap-2">
                  <Label>ประเภท Layout</Label>
                  <Select
                    value={filters.layoutMode}
                    onValueChange={(value) =>
                      setFilters((prev) => ({ ...prev, layoutMode: value as DashboardLayoutMode }))
                    }
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plot-plan">Plot Plan</SelectItem>
                      <SelectItem value="map">Map</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={handleSearch}
                className="gap-2"
                disabled={requiresWorkAreaForLayout}
              >
                <Search className="h-4 w-4" />
                ค้นหาข้อมูล
              </Button>
              <Button type="button" variant="outline" onClick={handleClear} className="gap-2">
                <RefreshCcw className="h-4 w-4" />
                ล้างตัวกรอง
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-4">
        <SummaryHeroCard
          title="Total Permit"
          value={isLoadingCounts ? '...' : statusSummary.total}
          toneClassName="border-emerald-300 bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 text-white"
          onClick={handleTotalPermitCardClick}
          active={appliedFilters.permitType === 'ALL' && appliedFilters.statusCategory === 'ALL'}
        />
        <SummaryHeroCard
          title="Cold Work"
          value={isLoadingCounts ? '...' : permitTypeSummary.cold}
          toneClassName="border-blue-300 bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 text-white"
          onClick={() => applyCardFilter('permitType', CARD_PERMIT_TYPE_CODES.coldWork)}
          active={appliedFilters.permitType === CARD_PERMIT_TYPE_CODES.coldWork}
        />
        <SummaryHeroCard
          title="Confined Space"
          value={isLoadingCounts ? '...' : permitTypeSummary.confine}
          toneClassName="border-amber-300 bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 text-amber-950"
          onClick={() => applyCardFilter('permitType', CARD_PERMIT_TYPE_CODES.confineWork)}
          active={appliedFilters.permitType === CARD_PERMIT_TYPE_CODES.confineWork}
        >
          <div className="grid grid-cols-2 gap-2">
            <SummarySubCard
              title="Inert Gas Area"
              value={isLoadingCounts ? '...' : permitTypeSummary.confineInertGasArea}
              className="bg-orange-600/90 text-white"
              onClick={(event) => {
                event.stopPropagation()
                applySubCardFilter(CARD_PERMIT_TYPE_CODES.confineWork, 'inertGasArea', CARD_INERT_GAS_VALUES.inertGasArea)
              }}
              active={appliedFilters.permitType === CARD_PERMIT_TYPE_CODES.confineWork && appliedFilters.inertGasArea === CARD_INERT_GAS_VALUES.inertGasArea}
            />
            <SummarySubCard
              title="Non Inert Gas Area"
              value={isLoadingCounts ? '...' : permitTypeSummary.confineNonInertGasArea}
              className="bg-orange-700/90 text-white"
              onClick={(event) => {
                event.stopPropagation()
                applySubCardFilter(CARD_PERMIT_TYPE_CODES.confineWork, 'inertGasArea', CARD_INERT_GAS_VALUES.nonInertGasArea)
              }}
              active={appliedFilters.permitType === CARD_PERMIT_TYPE_CODES.confineWork && appliedFilters.inertGasArea === CARD_INERT_GAS_VALUES.nonInertGasArea}
            />
          </div>
        </SummaryHeroCard>
        <SummaryHeroCard
          title="Hot Work"
          value={isLoadingCounts ? '...' : permitTypeSummary.hot}
          toneClassName="border-rose-300 bg-gradient-to-br from-rose-400 via-red-500 to-red-600 text-white"
          onClick={() => applyCardFilter('permitType', CARD_PERMIT_TYPE_CODES.hotWork)}
          active={appliedFilters.permitType === CARD_PERMIT_TYPE_CODES.hotWork}
        >
          <div className="grid grid-cols-2 gap-2">
            <SummarySubCard
              title="Open Fire"
              value={isLoadingCounts ? '...' : permitTypeSummary.hotOpenFire}
              className="bg-red-700/90 text-white"
              onClick={(event) => {
                event.stopPropagation()
                applySubCardFilter(CARD_PERMIT_TYPE_CODES.hotWork, 'toolType', CARD_TOOL_TYPE_VALUES.openFire)
              }}
              active={appliedFilters.permitType === CARD_PERMIT_TYPE_CODES.hotWork && appliedFilters.toolType === CARD_TOOL_TYPE_VALUES.openFire}
            />
            <SummarySubCard
              title="Non Open Fire"
              value={isLoadingCounts ? '...' : permitTypeSummary.hotNonOpenFire}
              className="bg-red-800/90 text-white"
              onClick={(event) => {
                event.stopPropagation()
                applySubCardFilter(CARD_PERMIT_TYPE_CODES.hotWork, 'toolType', CARD_TOOL_TYPE_VALUES.nonOpenFire)
              }}
              active={appliedFilters.permitType === CARD_PERMIT_TYPE_CODES.hotWork && appliedFilters.toolType === CARD_TOOL_TYPE_VALUES.nonOpenFire}
            />
          </div>
        </SummaryHeroCard>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        <SummaryStatusCard
          title="Wait For Open"
          subtitle="รอเปิด Permit"
          value={isLoadingCounts ? '...' : statusSummary.waitOpen}
          valueClassName="text-amber-500"
          cardClassName="border-amber-200 bg-amber-50/70"
          onClick={() => applyCardFilter('statusCategory', CARD_STATUS_CODES.waitForOpen)}
          active={appliedFilters.statusCategory === CARD_STATUS_CODES.waitForOpen}
        />
        <SummaryStatusCard
          title="Opened"
          subtitle="เปิดใช้งานแล้ว"
          value={isLoadingCounts ? '...' : statusSummary.opened}
          valueClassName="text-emerald-500"
          cardClassName="border-emerald-200 bg-emerald-50/70"
          onClick={() => applyCardFilter('statusCategory', CARD_STATUS_CODES.opened)}
          active={appliedFilters.statusCategory === CARD_STATUS_CODES.opened}
        />
        <SummaryStatusCard
          title="Closed"
          subtitle="ปิดแล้ว"
          value={isLoadingCounts ? '...' : statusSummary.closed}
          valueClassName="text-slate-500"
          cardClassName="border-slate-200 bg-slate-50/80"
          onClick={() => applyCardFilter('statusCategory', CARD_STATUS_CODES.closed)}
          active={appliedFilters.statusCategory === CARD_STATUS_CODES.closed}
        />
        <SummaryStatusCard
          title="Cancel"
          subtitle="ยกเลิก"
          value={isLoadingCounts ? '...' : statusSummary.cancelled}
          valueClassName="text-rose-500"
          cardClassName="border-rose-200 bg-rose-50/70"
          onClick={() => applyCardFilter('statusCategory', CARD_STATUS_CODES.cancelled)}
          active={appliedFilters.statusCategory === CARD_STATUS_CODES.cancelled}
        />
        <Card
          className="cursor-pointer border-blue-200 bg-blue-50/70 shadow-sm transition-transform hover:scale-[1.02]"
          onClick={() => void handleOpenWorkerDialog()}
        >
          <CardContent className="flex items-start justify-between gap-4 p-4">
            <div>
              <p className="text-sm font-medium text-blue-700">Total Worker</p>
              <p className="mt-1 text-[11px] text-blue-500">จำนวนคนงานที่แสดง (คลิกดูรายชื่อ)</p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold leading-none text-indigo-500">
                {isLoadingCounts ? '...' : statusSummary.workers}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {appliedFilters.showLayout ? (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex flex-wrap items-center gap-2 text-lg text-slate-900">
              {appliedFilters.layoutMode === 'plot-plan' ? (
                <ImageIcon className="h-5 w-5 text-sky-600" />
              ) : (
                <MapPinned className="h-5 w-5 text-sky-600" />
              )}
              {appliedFilters.layoutMode === 'plot-plan' ? 'Layout พื้นที่ทำงาน (Plot Plan)' : 'Layout พื้นที่ทำงาน (Map)'}
              <span className="text-sm font-normal text-slate-500">
                {appliedFilters.dateFrom || appliedFilters.dateTo ? (
                  <>
                    <CalendarDays className="mr-1 inline h-4 w-4" />
                    {appliedFilters.dateFrom || '-'} ถึง {appliedFilters.dateTo || '-'}
                  </>
                ) : null}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {layoutPanelContent}
          </CardContent>
        </Card>
      ) : null}

      {renderPaginationBar()}

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 pb-4">
          <CardTitle className="text-lg text-slate-900">รายการใบอนุญาต ({permits.length} รายการ)</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleExportExcel()}
            disabled={isExportingExcel || isLoading}
          >
            {isExportingExcel ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Export Excel
          </Button>
        </CardHeader>
        <CardContent>
          {errorMessage ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {errorMessage}
            </div>
          ) : null}

          {isLoading ? (
            <div className="flex min-h-[160px] items-center justify-center text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              กำลังโหลดข้อมูล
            </div>
          ) : permits.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-600">
              ไม่พบข้อมูล Permit ตามเงื่อนไขที่เลือก
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">Permit No</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">รายละเอียดงาน</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">พื้นที่ปฏิบัติงาน</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">ผู้ขอ</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">วันที่ปฏิบัติงาน</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">สถานะ</th>
                    <th className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-600">ดูข้อมูล</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {permits.map((permit) => (
                    <tr key={permit.permitId} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-4 py-4 align-top">
                        <div className="font-semibold text-slate-900">{permit.permitNumber}</div>
                        <div className="text-xs text-slate-500">สร้างเมื่อ {formatWorkDate(permit.createdAt)}</div>
                        <Badge variant="outline" className={`mt-1 ${getPermitTypeBadgeClass(permit.permitType)}`}>
                          {permit.permitType || '-'}
                        </Badge>
                      </td>
                      <td className="max-w-[260px] px-4 py-4 align-top text-slate-700">
                        <div
                          className="break-words leading-5"
                          style={twoLineClampStyle}
                          title={permit.workDescription || permit.title || '-'}
                        >
                          {permit.workDescription || permit.title || '-'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-slate-700">
                        <div
                          className="break-words leading-5"
                          style={twoLineClampStyle}
                          title={resolvePermitLocationLabel(permit)}
                        >
                          {resolvePermitLocationLabel(permit)}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-slate-700">
                        <div className="text-slate-900">{permit.requesterName || '-'}</div>
                        <div className="text-xs text-slate-500">{permit.requesterCompany || '-'}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-slate-700">
                        {formatWorkPeriod(permit.plannedStart, permit.plannedEnd)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 align-top">
                        <Badge variant="outline" className={getPermitStatusBadgeClass(permit.status)}>
                          {getPermitStatusLabel(permit.status, permit.statusName)}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 align-top text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const params = new URLSearchParams({
                              permitId: permit.permitId,
                              mode: 'edit',
                              entryMode: 'display',
                              forceReadOnly: 'true',
                              hideActionBar: 'true',
                              tenantId,
                            })
                            window.open(`/permit-request?${params.toString()}`, '_blank', 'noopener')
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          เปิดดู
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

            </div>
          )}
        </CardContent>
      </Card>

      {renderPaginationBar()}

      <Dialog open={Boolean(selectedRecord)} onOpenChange={(open) => !open && setSelectedRecord(null)}>
        <DialogContent className="z-[1100] max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedRecord?.permit.permitNumber ?? 'Permit Detail'}</DialogTitle>
            <DialogDescription>
              {selectedRecord?.permit.title || selectedRecord?.permit.workDescription || '-'}
            </DialogDescription>
          </DialogHeader>

          {selectedRecord ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">สถานะ</p>
                  <Badge variant="outline" className={`mt-1 ${getPermitStatusBadgeClass(selectedRecord.permit.status)}`}>
                    {getPermitStatusLabel(selectedRecord.permit.status, selectedRecord.permit.statusName)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">ประเภท Permit</p>
                  <p className="mt-1 font-medium text-slate-900">{selectedRecord.permit.permitType || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">พื้นที่ปฏิบัติงาน</p>
                  <p className="mt-1 font-medium text-slate-900">
                    {resolvePermitLocationLabel(selectedRecord.permit)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">ช่วงเวลาปฏิบัติงาน</p>
                  <p className="mt-1 font-medium text-slate-900">
                    {formatWorkPeriod(selectedRecord.permit.plannedStart, selectedRecord.permit.plannedEnd)}
                  </p>
                </div>
              </div>

              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">ผู้ขอ</p>
                  <p className="mt-1 font-medium text-slate-900">{selectedRecord.permit.requesterName || '-'}</p>
                  <p className="text-sm text-slate-500">{selectedRecord.permit.requesterCompany || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">บริษัทตาม Filter</p>
                  <p className="mt-1 font-medium text-slate-900">{selectedCompanyLabel}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">จำนวนผู้ปฏิบัติงาน</p>
                  <p className="mt-1 font-medium text-slate-900">{selectedRecord.detail?.workers?.length ?? 0} คน</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">โหมด Layout</p>
                  <p className="mt-1 font-medium text-slate-900">
                    {selectedRecord.detail?.workArea?.mapMode?.toLowerCase() === 'gps' ? 'Map / GPS' : 'Plot Plan'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={isWorkerDialogOpen} onOpenChange={setIsWorkerDialogOpen}>
        <DialogContent className="z-[1100] max-w-7xl">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle>รายชื่อผู้ปฏิบัติงาน ({workerRows.length} คน)</DialogTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleExportWorkersExcel()}
                disabled={isExportingWorkers || isLoadingWorkers || workerRows.length === 0}
              >
                {isExportingWorkers ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Export Excel
              </Button>
            </div>
            <DialogDescription>
              รายชื่อผู้ปฏิบัติงานที่ระบุไว้ในเอกสาร Work Permit ตามรายการที่เรียกดู
            </DialogDescription>
          </DialogHeader>

          {isLoadingWorkers ? (
            <div className="flex min-h-[160px] items-center justify-center text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              กำลังโหลดข้อมูล
            </div>
          ) : workerRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-600">
              ไม่พบผู้ปฏิบัติงานตามเงื่อนไขที่เลือก
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto rounded-xl border">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">ชื่อ</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">บริษัท</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">หน้าที่ความรับผิดชอบ</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">พื้นที่</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">Permit No</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-600">สถานะ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {workerRows.map((worker, index) => (
                    <tr key={`${worker.permitId}-${worker.workerId}-${index}`} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-900">{worker.workerName || '-'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">{worker.workerCompany || '-'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">{worker.workerRole || '-'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">{worker.area || '-'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">{worker.permitNumber}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <Badge variant="outline" className={getPermitStatusBadgeClass(worker.status)}>
                          {getPermitStatusLabel(worker.status, worker.statusName)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
